
import { useState } from "react";
import { TimeEntry } from "./TimeEntryForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { format } from "date-fns";
import { Search, Clock, Trash2, Eye, Pencil } from "lucide-react";
import { Project } from "@/types/Project";
import { TimeEntryEditDialog } from "./TimeEntryEditDialog";

interface TimeEntriesListProps {
  entries: TimeEntry[];
  onDelete: (id: string) => void;
  onUpdate?: (id: string, entry: Partial<TimeEntry>) => void;
  projects?: Project[];
  hideDescriptions?: boolean;
}

export function TimeEntriesList({ 
  entries, 
  onDelete,
  onUpdate,
  projects = [],
  hideDescriptions = true
}: TimeEntriesListProps) {
  const [search, setSearch] = useState("");
  const [expandedEntries, setExpandedEntries] = useState<Record<string, boolean>>({});
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);

  const filteredEntries = entries.filter(
    (entry) =>
      entry.description.toLowerCase().includes(search.toLowerCase()) ||
      projects?.find(p => p.id === entry.projectId)?.name.toLowerCase().includes(search.toLowerCase())
  );

  const getProjectName = (projectId?: string) => {
    if (!projectId) return undefined;
    return projects.find(p => p.id === projectId)?.name;
  };

  const toggleDescription = (id: string) => {
    setExpandedEntries(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  return (
    <div className="space-y-4">
      <div className="relative">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          placeholder="Search entries..."
        />
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      </div>

      <div className="space-y-4">
        {filteredEntries.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No time entries found
          </div>
        ) : (
          filteredEntries.map((entry) => (
            <div
              key={entry.id}
              className="glass-card p-4 rounded-lg hover-scale animate-in"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      {format(entry.date, "PPP")}
                    </span>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {Math.floor(entry.minutes / 60)}h {entry.minutes % 60}m
                    </div>
                  </div>
                  {getProjectName(entry.projectId) && (
                    <div className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium">
                      {getProjectName(entry.projectId)}
                    </div>
                  )}
                  {((!hideDescriptions || expandedEntries[entry.id]) && entry.description) && (
                    <p className="text-sm mt-1">{entry.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {hideDescriptions && entry.description && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleDescription(entry.id)}
                      className="text-muted-foreground"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  )}
                  {onUpdate && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditingEntry(entry)}
                      className="text-muted-foreground"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDelete(entry.id)}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {editingEntry && onUpdate && (
        <TimeEntryEditDialog
          entry={editingEntry}
          projects={projects}
          open={!!editingEntry}
          onOpenChange={(open) => !open && setEditingEntry(null)}
          onSave={onUpdate}
        />
      )}
    </div>
  );
}
